// src/pages/agro/Vacunas.js
import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'

const EMPTY = {
  animal_id: '', tipo_vacuna_id: '', fecha_aplicacion: new Date().toISOString().slice(0,10),
  fecha_proxima: '', dosis_ml: '', lote: '', estado: 'aplicada', notas: ''
}

export default function Vacunas() {
  const { profile } = useAuth()
  const canWrite = ['admin', 'veterinario'].includes(profile?.rol)

  const [vacunas, setVacunas] = useState([])
  const [animales, setAnimales] = useState([])
  const [tipos, setTipos] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [editId, setEditId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filtro, setFiltro] = useState('todos') // proximas, vencidas, todos

  useEffect(() => { loadData() }, [])

  async function loadData() {
    setLoading(true)
    const [vRes, aRes, tRes] = await Promise.all([
      supabase.from('vacunaciones').select(`
        *, animal:animales(codigo, nombre), tipo_vacuna:tipos_vacuna(nombre)
      `).order('fecha_aplicacion', { ascending: false }),
      supabase.from('animales').select('id, codigo, nombre').eq('estado', 'activo').order('codigo'),
      supabase.from('tipos_vacuna').select('*').order('nombre'),
    ])
    setVacunas(vRes.data ?? [])
    setAnimales(aRes.data ?? [])
    setTipos(tRes.data ?? [])
    setLoading(false)
  }

  function openNew() { setForm(EMPTY); setEditId(null); setModal(true) }
  function openEdit(v) {
    setForm({
      animal_id: v.animal_id, tipo_vacuna_id: v.tipo_vacuna_id,
      fecha_aplicacion: v.fecha_aplicacion, fecha_proxima: v.fecha_proxima ?? '',
      dosis_ml: v.dosis_ml ?? '', lote: v.lote ?? '', estado: v.estado, notas: v.notas ?? ''
    })
    setEditId(v.id); setModal(true)
  }

  function onChange(e) { setForm(f => ({ ...f, [e.target.name]: e.target.value })) }

  async function save() {
    if (!form.animal_id || !form.tipo_vacuna_id || !form.fecha_aplicacion) return alert('Animal, tipo y fecha son obligatorios')
    setSaving(true)
    const payload = { ...form, veterinario_id: profile.id }
    if (editId) await supabase.from('vacunaciones').update(payload).eq('id', editId)
    else await supabase.from('vacunaciones').insert([payload])
    setSaving(false); setModal(false); loadData()
  }

  const today = new Date().toISOString().slice(0,10)
  const in30 = new Date(Date.now() + 30*24*60*60*1000).toISOString().slice(0,10)

  const filtered = vacunas.filter(v => {
    if (filtro === 'proximas') return v.fecha_proxima && v.fecha_proxima >= today && v.fecha_proxima <= in30
    if (filtro === 'vencidas') return v.fecha_proxima && v.fecha_proxima < today && v.estado !== 'aplicada'
    return true
  })

  const estadoBadge = { aplicada: 'badge-green', pendiente: 'badge-amber', vencida: 'badge-red' }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">💉 Vacunas</div>
          <div className="page-subtitle">Control sanitario y calendario de vacunación</div>
        </div>
        {canWrite && <button className="btn btn-primary" onClick={openNew}>+ Registrar vacuna</button>}
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {['todos','proximas','vencidas'].map(f => (
            <button key={f} className={`btn btn-sm ${filtro === f ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFiltro(f)}>
              {f === 'todos' ? 'Todos' : f === 'proximas' ? '⚠️ Próximas 30d' : '🔴 Vencidas'}
            </button>
          ))}
          <span className="text-muted text-sm" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>{filtered.length} registros</span>
        </div>
        <div className="table-wrap">
          {loading
            ? <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner spinner-dark" style={{ margin: 'auto' }} /></div>
            : (
              <table>
                <thead>
                  <tr>
                    <th>Animal</th>
                    <th>Vacuna</th>
                    <th>Aplicación</th>
                    <th>Próxima</th>
                    <th>Dosis</th>
                    <th>Estado</th>
                    {canWrite && <th>Acciones</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && <tr><td colSpan={7}><div className="empty-state">Sin registros</div></td></tr>}
                  {filtered.map(v => (
                    <tr key={v.id}>
                      <td><span className="mono">{v.animal?.codigo}</span> {v.animal?.nombre && `(${v.animal.nombre})`}</td>
                      <td>{v.tipo_vacuna?.nombre}</td>
                      <td>{new Date(v.fecha_aplicacion).toLocaleDateString('es')}</td>
                      <td>
                        {v.fecha_proxima
                          ? <span style={{ color: v.fecha_proxima < today ? 'var(--accent-red)' : 'inherit' }}>
                              {new Date(v.fecha_proxima).toLocaleDateString('es')}
                            </span>
                          : '—'}
                      </td>
                      <td>{v.dosis_ml ? `${v.dosis_ml} ml` : '—'}</td>
                      <td><span className={`badge ${estadoBadge[v.estado]}`}>{v.estado}</span></td>
                      {canWrite && (
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(v)}>✏️</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{editId ? 'Editar vacunación' : 'Registrar vacunación'}</div>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Animal *</label>
                <select className="form-select" name="animal_id" value={form.animal_id} onChange={onChange}>
                  <option value="">Seleccionar...</option>
                  {animales.map(a => <option key={a.id} value={a.id}>{a.codigo} {a.nombre ? `(${a.nombre})` : ''}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tipo de vacuna *</label>
                <select className="form-select" name="tipo_vacuna_id" value={form.tipo_vacuna_id} onChange={onChange}>
                  <option value="">Seleccionar...</option>
                  {tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Fecha aplicación *</label>
                <input className="form-input" type="date" name="fecha_aplicacion" value={form.fecha_aplicacion} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Próxima aplicación</label>
                <input className="form-input" type="date" name="fecha_proxima" value={form.fecha_proxima} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Dosis (ml)</label>
                <input className="form-input" type="number" step="0.1" name="dosis_ml" value={form.dosis_ml} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Lote</label>
                <input className="form-input" name="lote" value={form.lote} onChange={onChange} />
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" name="estado" value={form.estado} onChange={onChange}>
                  <option value="aplicada">Aplicada</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="vencida">Vencida</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea className="form-textarea" name="notas" value={form.notas} onChange={onChange} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
